import { useEffect, useRef } from 'react';

import { useSession } from '@/hooks/authHooks';
import {
  SubscriptionTier,
  useUpdateSubscription,
} from '@/hooks/useSubscription';

/**
 * SubscriptionSyncHandler - Handles post-auth subscription creation
 *
 * When a user selects a plan on the upgrade page and then signs up,
 * we store the selected plan in sessionStorage. After auth is complete,
 * this component checks for a pending plan selection and creates the subscription.
 */
export function SubscriptionSyncHandler() {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const { mutate, isPending: isMutating } = useUpdateSubscription();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Don't run if still loading, already processed, or mutation in progress
    if (isPending || hasProcessedRef.current || isMutating) {
      return;
    }

    // Only run for authenticated users
    if (!isAuthenticated) {
      return;
    }

    // Check for pending plan selection
    const selectedPlan = sessionStorage.getItem('selectedPlan');
    if (!selectedPlan) {
      return;
    }

    // Validate the plan
    const validPlans: SubscriptionTier[] = ['free', 'pro', 'premium'];
    if (!validPlans.includes(selectedPlan as SubscriptionTier)) {
      sessionStorage.removeItem('selectedPlan');
      return;
    }

    // Mark as processed to prevent multiple calls
    hasProcessedRef.current = true;

    // Create/update the subscription
    mutate(selectedPlan as SubscriptionTier, {
      onSuccess: () => {
        // Only clear after successful creation
        sessionStorage.removeItem('selectedPlan');
      },
      onError: (error) => {
        console.error('Failed to create subscription:', error);
        // Reset so it can be retried
        hasProcessedRef.current = false;
      },
    });
  }, [isAuthenticated, isPending, isMutating, mutate]);

  return null;
}
